import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';

import Shell from './components/layout/Shell';
import AuditorShell from './components/layout/AuditorShell';
import BookkeeperShell from './components/layout/BookkeeperShell';
import StoreUserShell from './components/layout/StoreUserShell';
import SuperAdminShell from './components/layout/SuperAdminShell';

import DashboardPage from './pages/DashboardPage';
import ModulePage from './pages/ModulePage';
import StatementUploadPage from './pages/StatementUploadPage';
import AuditTrailPage from './pages/AuditTrailPage';
import OrgSummaryPage from './pages/OrgSummaryPage';
import RolePickerPage from './pages/RolePickerPage';
import LoginPage from './pages/LoginPage';
import Profile from './pages/Profile';

import AuditorDashboardPage from './pages/auditor/AuditorDashboardPage';
import VendorProfilesPage from './pages/auditor/VendorProfilesPage';
import VendorsNeededPage from './pages/auditor/VendorsNeededPage';
import CodChecksPage from './pages/auditor/CodChecksPage';
import CoreChargesPage from './pages/auditor/CoreChargesPage';
import DailyAuditPage from './pages/auditor/dailyAudit/DailyAuditPage';
import DailyAuditDatePage from './pages/auditor/dailyAudit/DailyAuditDatePage';
import DailyAuditPathwayPage from './pages/auditor/dailyAudit/DailyAuditPathwayPage';
import DocumentReviewQueuePage from './pages/auditor/dailyAudit/DocumentReviewQueuePage';
import BookkeeperDashboardPage from './pages/bookkeeper/BookkeeperDashboardPage';
import StoreUserDashboardPage from './pages/store/StoreUserDashboardPage';
import SuperAdminDashboardPage from './pages/admin/SuperAdminDashboardPage';

// Month-End Close (MEC) module — see MEC_REQUIREMENTS.md §7
import MecShell from './components/mec/MecShell';
import MecDashboard from './pages/MonthEndClose/Dashboard';
import Today from './pages/MonthEndClose/Today';
import MecPlaceholderPage from './pages/MonthEndClose/PlaceholderPage';
import AccountRouting from './pages/MonthEndClose/Settings/AccountRouting';
import MatchModeSettings from './pages/MonthEndClose/Settings/MatchMode';
import Locations from './pages/MonthEndClose/Locations';
import LocationDrillDown from './pages/MonthEndClose/LocationDrillDown';
import Tier2Review from './pages/MonthEndClose/Tier2Review';
import JournalEntry from './pages/MonthEndClose/JournalEntry';

// QuickBooks Reporting — read-only CoA compare + future P&L.
// Lazy-loaded so any error in this product's chunks CANNOT crash the React
// app on boot for Hub / MEC users. Silo invariant: a syntax or runtime error
// in QBO Reporting only affects users who navigate INTO /qbo-reporting/*.
const QboReportingShell = lazy(() => import('./components/qboReporting/QboReportingShell'));
const ChartComparison = lazy(() => import('./pages/QboReporting/ChartComparison'));
const ActionWorksheet = lazy(() => import('./pages/QboReporting/ActionWorksheet'));
const ProfitLoss = lazy(() => import('./pages/QboReporting/ProfitLoss'));

function QboReportingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center text-[13px] text-[var(--color-warm-500)]">
      Loading QuickBooks Reporting…
    </div>
  );
}

import RequireAuth from './components/auth/RequireAuth';

function RoleRedirect() {
  const { user, loading, isAuthenticated } = useAuth();
  if (loading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  const home = {
    super_admin: '/admin',
    bookkeeper: '/bookkeeper',
    auditor: '/auditor',
    store_user: '/store',
  }[user?.role] || '/roles';
  return <Navigate to={home} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        {/* Public */}
        <Route path="/login" element={<LoginPage />} />

        {/* Root → role-based redirect */}
        <Route path="/" element={<RoleRedirect />} />

        {/* Role picker (dev only, helpful until auth is distributed) */}
        <Route element={<RequireAuth />}>
          <Route path="/roles" element={<RolePickerPage />} />
          <Route path="/profile" element={<Profile />} />
        </Route>

        {/* Super Admin */}
        <Route element={<RequireAuth allowedRoles={['super_admin']} />}>
          <Route path="/admin" element={<SuperAdminShell />}>
            <Route index element={<SuperAdminDashboardPage />} />
          </Route>
        </Route>

        {/* Bookkeeper */}
        <Route element={<RequireAuth allowedRoles={['super_admin', 'bookkeeper']} />}>
          <Route path="/bookkeeper" element={<BookkeeperShell />}>
            <Route index element={<BookkeeperDashboardPage />} />
          </Route>
        </Route>

        {/* Auditor */}
        <Route element={<RequireAuth allowedRoles={['super_admin', 'auditor']} />}>
          <Route path="/auditor" element={<AuditorShell />}>
            <Route index element={<AuditorDashboardPage />} />
            <Route path="daily-audit" element={<DailyAuditPage />} />
            <Route path="daily-audit/:date" element={<DailyAuditDatePage />} />
            <Route path="daily-audit/:date/cod-checks" element={<DailyAuditPathwayPage />} />
            <Route path="daily-audit/:date/cod-checks/review" element={<DocumentReviewQueuePage />} />
            {/* Legacy URLs — redirect to Daily Audit */}
            <Route path="cod-checks" element={<Navigate to="/auditor/daily-audit" replace />} />
            <Route path="document-review" element={<Navigate to="/auditor/daily-audit" replace />} />
            {/* Old direct page kept reachable via /auditor/cod-checks-legacy for fallback */}
            <Route path="cod-checks-legacy" element={<CodChecksPage />} />
            <Route path="core-charges" element={<CoreChargesPage />} />
            <Route path="vendors" element={<VendorProfilesPage />} />
            <Route path="vendors-needed" element={<VendorsNeededPage />} />
          </Route>
        </Route>

        {/* Store User */}
        <Route element={<RequireAuth allowedRoles={['super_admin', 'store_user']} />}>
          <Route path="/store" element={<StoreUserShell />}>
            <Route index element={<StoreUserDashboardPage />} />
          </Route>
        </Route>

        {/* Month-End Close (MEC) — any authenticated user can READ; mutations
            are gated server-side. See MEC_REQUIREMENTS.md §7. */}
        <Route element={<RequireAuth />}>
          <Route path="/mec" element={<MecShell />}>
            <Route index element={<MecDashboard />} />
            <Route path="today" element={<Today />} />
            <Route path="settings/routing" element={<AccountRouting />} />
            <Route path="settings/mode" element={<MatchModeSettings />} />
            <Route path="locations" element={<Locations />} />
            <Route path="location/:shop_id" element={<LocationDrillDown />} />
            <Route path="tier-2-review" element={<Tier2Review />} />
            <Route path="journal-entry" element={<JournalEntry />} />
          </Route>
        </Route>

        {/* QuickBooks Reporting — read-only CoA compare + (future) P&L.
            Lazy-loaded + Suspense-wrapped so failures here can't reach Hub/MEC. */}
        <Route element={<RequireAuth allowedRoles={['super_admin', 'bookkeeper']} />}>
          <Route
            path="/qbo-reporting"
            element={
              <Suspense fallback={<QboReportingFallback />}>
                <QboReportingShell />
              </Suspense>
            }
          >
            <Route index element={<Navigate to="/qbo-reporting/chart-of-accounts" replace />} />
            <Route
              path="chart-of-accounts"
              element={
                <Suspense fallback={<QboReportingFallback />}>
                  <ChartComparison />
                </Suspense>
              }
            />
            <Route
              path="action-worksheet"
              element={
                <Suspense fallback={<QboReportingFallback />}>
                  <ActionWorksheet />
                </Suspense>
              }
            />
            <Route
              path="profit-loss"
              element={
                <Suspense fallback={<QboReportingFallback />}>
                  <ProfitLoss />
                </Suspense>
              }
            />
          </Route>
        </Route>

        {/* Legacy Shell (deprecated, will retire after Phase H) */}
        <Route element={<RequireAuth />}>
          <Route path="/legacy" element={<Shell />}>
            <Route index element={<DashboardPage />} />
            <Route path="module/:moduleKey" element={<ModulePage />} />
            <Route path="statements" element={<StatementUploadPage />} />
            <Route path="audit-trail" element={<AuditTrailPage />} />
            <Route path="org" element={<OrgSummaryPage />} />
          </Route>
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  );
}
